import { PlannedOrder } from "@/reducers/plannedOrdersSlice"
import { type TimelineChain } from "../buildTimelineData"

// Planner role sees every dispatcher's parked orders; a regular dispatcher
// only ever sees their own — this is the single scoping rule the whole
// Planner view (lanes, tally badges, "Przypisane do dyspozytora" list)
// applies once the planner-role flag is off.
export const scopeOrdersToDispatcher = (
  orders: PlannedOrder[],
  isPlannerRoleEnabled: boolean,
  currentUserJid: string | undefined,
): PlannedOrder[] =>
  isPlannerRoleEnabled
    ? orders
    : orders.filter((order) => order.assignedDispatcherJid === currentUserJid)

export const matchesCompanyAndDispatcherFilter = (
  order: PlannedOrder,
  companyFilter: string[],
  dispatcherFilter: string[],
): boolean => {
  if (
    companyFilter.length > 0 &&
    !companyFilter.includes(order.metadata?.customer_name ?? "")
  ) {
    return false
  }
  if (
    dispatcherFilter.length > 0 &&
    !dispatcherFilter.includes(order.assignedDispatcherJid ?? "")
  ) {
    return false
  }
  return true
}

// Companies a vehicle works for: the customer of every transport on its
// timeline (current and future work, plus whatever completed work the
// provider feed still reports) together with the customer of every planned
// order a dispatcher has already put on it. Both sources are needed — a
// vehicle can be driving for a company that has nothing in the pool, and an
// order can be planned onto a vehicle that isn't rolling for that company
// yet.
export const vehicleCompanyNames = (
  chains: Pick<TimelineChain, "customer">[],
  assignedOrders: PlannedOrder[],
): string[] => {
  const names = new Set<string>()
  for (const chain of chains) {
    if (chain.customer) names.add(chain.customer)
  }
  for (const order of assignedOrders) {
    const name = order.metadata?.customer_name
    if (name) names.add(name)
  }
  return Array.from(names)
}

// A vehicle with no known company never survives a company filter: picking a
// company means "show me only this company's vehicles", and an empty grid is
// the honest answer when none qualify. Only feeds that carry the customer
// (Navigator) can satisfy this at all — G-Box errands don't know it.
export const matchesVehicleCompanyFilter = (
  companyFilter: string[],
  companyNames: string[],
): boolean =>
  companyFilter.length === 0 ||
  companyNames.some((name) => companyFilter.includes(name))

// Firmy, którymi dyspozytor sam zadeklarował, że się opiekuje (ustawienie
// `planner_companies`). PUSTY WYBÓR ZNACZY "POKAŻ WSZYSTKO" — to preferencja
// widoku, nie uprawnienie, więc nikt nie może stracić zleceń z oczu wbrew
// sobie.
//
// Porównanie jest dosłowne, tylko trim i wielkość liter: wybór pochodzi z
// kartoteki Marcosa i zapisuje ten sam ShortName[:30], który trafia na
// zlecenie. Trim jest konieczny, bo w kartotece są nazwy z końcową spacją
// ('BA GLASS POLAND ', 'MAGNISI '). Nie normalizujemy form prawnych ani
// nawiasów — to byłoby zgadywanie, a oba stringi pochodzą z tego samego pola.
export const foldCompanyName = (name: string): string =>
  name.trim().toLowerCase()

export const matchesMyCompanies = (
  order: PlannedOrder,
  myCompanyNames: string[],
  currentUserJid: string | undefined,
): boolean => {
  if (myCompanyNames.length === 0) return true
  // Zlecenie przypisane do mnie lub przeze mnie stworzone jest zawsze widoczne
  // — nawet gdy firma wykracza poza mój wybór. Przypadki: kolega przypisał mi
  // zlecenie innej firmy, lub stworzyłem zlecenie ręcznie (customer_name z PDFa
  // nigdy nie jest dokładnie ShortName[:30]).
  if (
    currentUserJid &&
    (order.createdBy === currentUserJid ||
      order.assignedDispatcherJid === currentUserJid)
  ) {
    return true
  }
  const customer = order.metadata?.customer_name
  if (typeof customer !== "string" || !customer) return true
  const wanted = new Set(myCompanyNames.map(foldCompanyName))
  return wanted.has(foldCompanyName(customer))
}
